import React, { useRef, useState, useEffect } from 'react'
import { ChromePicker } from 'react-color'

import Button from '~/Common-Objects/UI/Forms/Button/Button'
import ClickAway from '~/Common-Objects/ClickAway'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Icons from '~/Common-Objects/UI/Icons/index'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import axios from '~/data/http/axios'
import SiteVars from '~/Classes/siteVars'
import allowedFonts from '~/Classes/fonts'
import i18n from '~/i18n'
import TemplateExamples from './templateExamples'

export default function ThemeSelector({ job, setJob = () => {}, save = () => {}, next = () => {}, prev = () => {} }) {
    console.log('job in theme selector', job)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const fonts = allowedFonts.map((f) => {
        return {
            label: f.label,
            value: f.value
        }
    })

    const isFontAvailable = fonts.some((f) => f.value.toLowerCase() === job.results.fonts[0]?.toLowerCase())

    const themeFont = allowedFonts.find((f) => f.value.toLowerCase() === (job.results.fonts[0] || '').toLowerCase())
    const defaultFont = allowedFonts.find((f) => f.value.trim().toLowerCase().includes('inter')) || allowedFonts[0]

    // Check for selected fonts first, then fall back to detected/default fonts
    const getSelectedHeadingFont = () => {
        if (job.selected?.fonts?.heading) {
            const selectedFont = allowedFonts.find(
                (f) => f.value.toLowerCase() === job.selected.fonts.heading.toLowerCase()
            )
            if (selectedFont) return selectedFont.value
        }
        return isFontAvailable && themeFont ? themeFont.value : defaultFont ? defaultFont.value : fonts[0]?.value || ''
    }

    const getSelectedBodyFont = () => {
        if (job.selected?.fonts?.body) {
            const selectedFont = allowedFonts.find(
                (f) => f.value.toLowerCase() === job.selected.fonts.body.toLowerCase()
            )
            if (selectedFont) return selectedFont.value
        }
        return isFontAvailable && themeFont ? themeFont.value : defaultFont ? defaultFont.value : fonts[0]?.value || ''
    }

    const fileUploadInput = useRef()
    const [logoIndex, setLogoIndex] = useState(job.results.images.findIndex((i) => i.url == job.selected?.logo?.url))
    const [fileError, setFileError] = useState('')
    const [fileUploading, setFileUploading] = useState(false)
    const [headingFont, setHeadingFont] = useState(getSelectedHeadingFont())
    const [bodyFont, setBodyFont] = useState(getSelectedBodyFont())

    // Prioritize selected colors first, then fall back to detected colors
    const getSelectedPrimaryColor = () => {
        if (job.selected?.colours?.primary) return job.selected.colours.primary
        if (job.selected?.primary) return job.selected.primary
        return job.results.colors[0] || ''
    }

    const getSelectedTextColor = () => {
        if (job.selected?.colours?.text) return job.selected.colours.text
        if (job.selected?.text) return job.selected.text
        return job.results.colors[1] !== job.results.colors[0] ? job.results.colors[1] : '#000'
    }

    const getSelectedBackgroundColor = () => {
        if (job.selected?.colours?.background) return job.selected.colours.background
        if (job.selected?.background) return job.selected.background
        return job.results.colors[2] || ''
    }

    const [primaryColour, setPrimaryColour] = useState(getSelectedPrimaryColor())
    const [textColour, setTextColour] = useState(getSelectedTextColor())
    const [backgroundColour, setBackgroundColour] = useState(getSelectedBackgroundColor())
    const colourArr = [primaryColour, textColour, backgroundColour]

    const changeLogo = (dir) => {
        let newIndex = logoIndex + dir
        if (dir === 1 && newIndex >= job.results.images.length) newIndex = 0
        if (dir === -1 && newIndex < 0) newIndex = job.results.images.length - 1
        job.selected.logo = job.results.images[newIndex]
        setLogoIndex(newIndex)
        setJob(job)
    }

    const selectTheme = () => {
        const j = { ...job }
        const selected = { ...j.selected }
        selected.fonts = {
            body: bodyFont,
            heading: headingFont
        }
        selected.colours = {
            primary: primaryColour,
            text: textColour,
            background: backgroundColour
        }
        j.selected = selected
        setJob(j)
        save(j)
        next()
    }

    const uploadFileChanged = async (e) => {
        try {
            setFileError('')
            let files = []
            if (e.preventDefault) {
                e.preventDefault()
                files = e.target.files
            } else {
                files = e
            }

            if (!files.length) {
                setFileError('Invalid File - Please try again')
                return
            }
            setFileUploading(true)
            let file = files[0]
            var data = new FormData()
            data.append('file', file)
            data.append('friendlyName', file.name)

            let newFile = await axios.post('/fileManager/' + job.results.fileManagerFolderId + '/file', data, {
                onUploadProgress: (progressEvent) => uploadFileProgress(progressEvent)
            })
            setFileUploading(false)
            if (!newFile.data || !newFile.data.File) {
                return setFileError('Error uploading - Please try again')
            }

            //see if the logo already exists
            let fileName = newFile.data.File.fileName
            let fileUrl = SiteVars.repoEndPoint + newFile.data.File.filePath + newFile.data.File.fileName
            let newLogoIndex = job.results.images.findIndex((i) => i.url == fileUrl)
            if (newLogoIndex < 0) {
                job.results.images.push({
                    url: fileUrl,
                    name: fileName
                })
                newLogoIndex = job.results.images.length - 1
                job.selected.logo = job.results.images[newLogoIndex]
            } else {
                job.selected.logo = job.results.images[newLogoIndex]
            }
            setLogoIndex(newLogoIndex)
            setJob(job)
        } catch (err) {
            console.log(err)
            setFileError('Error uploading - Please try again')
        }
    }

    const clickUploadButton = () => {
        fileUploadInput.current.click()
    }

    let logo
    if (job.selected && job.selected.logo) {
        logo = job.selected.logo
    } else {
        logo = {
            url: 'https://cdn1.ourmailsender.com/userContent/6/fileManager/29429/logo-placeholder-black.png',
            name: ''
        }
        if (!job.selected) job.selected = {}
        job.selected.logo = logo
    }

    if (!colourArr || !job.selected || !logo) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '720px' }}>
                    <h4 className='mar-b25'>{i18n.t('templates:brandAnalyzer.themeSelectorPage.siteAnalyzer')}</h4>
                    <WarningHolder style={{ textAlign: 'left' }}>
                        <div className='quickFlex mar-b15'>
                            <Icons icon='alert' color='red' className='mar-r10' />
                            <h3>{i18n.t('templates:brandAnalyzer.themeSelectorPage.couldntFindWebsiteContent')}</h3>
                        </div>
                        <div className='mar-b15'>
                            {i18n.t('templates:brandAnalyzer.themeSelectorPage.couldntFindLogo')}
                        </div>
                        <Button className='primary' text='Try another URL' onClick={prev} />
                    </WarningHolder>
                </div>
            </div>
        )
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ maxWidth: '720px', width: '100%' }}>
                    <h5 className='mar-b10 text-center'>
                        {!!job.url && i18n.t('templates:brandAnalyzer.themeSelectorPage.headerText')}
                        {!job.url && i18n.t('templates:brandAnalyzer.themeSelectorPage.headerTextNoUrl')}
                    </h5>
                    <p className='mar-b25 text-center'>
                        {!!job.url && i18n.t('templates:brandAnalyzer.themeSelectorPage.subHeaderText')}
                        {!job.url && i18n.t('templates:brandAnalyzer.themeSelectorPage.subHeaderTextNoUrl')}
                    </p>
                </div>
                <div style={{ maxWidth: '1000px', width: '100%' }}>
                    <ThemeBlock
                        heading={i18n.t('templates:brandAnalyzer.themeSelectorPage.companyLogo.title')}
                        description={i18n.t('templates:brandAnalyzer.themeSelectorPage.companyLogo.description')}
                    >
                        <Paper
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '160px',
                                width: '320px',
                                backgroundImage:
                                    'url(https://cdn1.ourmailsender.com/siteContent/brandAnalyzer/transparentBg.png)',
                                backgroundRepeat: 'repeat',
                                padding: '32px 10px',
                                marginBottom: 0
                            }}
                        >
                            <div style={{ flex: 0 }}>
                                <Icons
                                    icon='arrowLeft'
                                    circle={true}
                                    onClick={() => {
                                        changeLogo(-1)
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '190px'
                                }}
                                className='mar-l10 mar-r10'
                            >
                                {logo.url && (
                                    <img
                                        src={logo.url}
                                        style={{ width: '100%', maxWidth: '140px', maxHeight: '140px' }}
                                    />
                                )}
                            </div>
                            <div style={{ flex: 0 }}>
                                <Icons
                                    icon='arrowRight'
                                    circle={true}
                                    onClick={() => {
                                        changeLogo(1)
                                    }}
                                />
                            </div>
                        </Paper>
                        <div>
                            <input
                                type='file'
                                ref={fileUploadInput}
                                style={{ display: 'none' }}
                                onChange={uploadFileChanged}
                            />
                            <Button
                                className='button-outline'
                                iconLeft={
                                    fileUploading ? <Icons icon='loading' /> : <Icons icon='cloudUpload' />
                                }
                                text={
                                    fileUploading
                                        ? i18n.t('templates:brandAnalyzer.themeSelectorPage.uploadingLogo')
                                        : i18n.t('templates:brandAnalyzer.themeSelectorPage.uploadDifferentLogo')
                                }
                                onClick={clickUploadButton}
                                postHogTag='brand-analyzer-upload-logo'
                            />
                            {fileError && fileError.length && (
                                <FormError key={'err'}>{fileError}</FormError>
                            )}
                        </div>
                    </ThemeBlock>
                    <ThemeBlock
                        heading={i18n.t('templates:brandAnalyzer.themeSelectorPage.emailTemplatePreview.title')}
                        description={i18n.t(
                            'templates:brandAnalyzer.themeSelectorPage.emailTemplatePreview.description'
                        )}
                    >
                        <TemplateExamples
                            colours={colourArr}
                            logo={logo}
                            headingFont={headingFont}
                            bodyFont={bodyFont}
                        />
                    </ThemeBlock>
                    <ThemeBlock
                        heading={i18n.t('templates:brandAnalyzer.themeSelectorPage.brandColours.title')}
                        description={i18n.t('templates:brandAnalyzer.themeSelectorPage.brandColours.description')}
                    >
                        <div
                            className='quickFlex'
                            style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}
                        >
                            <p className='text-heavy mar-b5'>
                                {i18n.t('templates:brandAnalyzer.themeSelectorPage.brandColours.primary')}
                            </p>
                            <ColourPicker colour={primaryColour} setColour={setPrimaryColour} />
                        </div>
                        <div
                            className='quickFlex'
                            style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}
                        >
                            <p className='text-heavy mar-b5'>
                                {i18n.t('templates:brandAnalyzer.themeSelectorPage.brandColours.text')}
                            </p>
                            <ColourPicker colour={textColour} setColour={setTextColour} />
                        </div>
                        <div
                            className='quickFlex'
                            style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}
                        >
                            <p className='text-heavy mar-b5'>
                                {i18n.t('templates:brandAnalyzer.themeSelectorPage.brandColours.background')}
                            </p>
                            <ColourPicker colour={backgroundColour} setColour={setBackgroundColour} />
                        </div>
                    </ThemeBlock>
                    <ThemeBlock
                        heading={i18n.t('templates:brandAnalyzer.themeSelectorPage.brandFonts.title')}
                        description={i18n.t('templates:brandAnalyzer.themeSelectorPage.brandFonts.description')}
                    >
                        <div
                            className='quickFlex'
                            style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}
                        >
                            <p className='text-heavy mar-b5'>
                                {i18n.t('templates:brandAnalyzer.themeSelectorPage.brandFonts.headingFont')}
                            </p>
                            <Select
                                filter={true}
                                data={fonts}
                                type='text'
                                name='headingFont'
                                value={headingFont}
                                onChange={(e) => setHeadingFont(e.target.value)}
                                outerStyle={{ width: '100%' }}
                            />
                        </div>
                        <div
                            className='quickFlex'
                            style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}
                        >
                            <p className='text-heavy mar-b5'>
                                {i18n.t('templates:brandAnalyzer.themeSelectorPage.brandFonts.bodyFont')}
                            </p>
                            <Select
                                filter={true}
                                data={fonts}
                                type='text'
                                name='bodyFont'
                                value={bodyFont}
                                onChange={(e) => setBodyFont(e.target.value)}
                                outerStyle={{ width: '100%' }}
                            />
                        </div>
                    </ThemeBlock>
                </div>
            </div>

            <div className='quickFlex' style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button
                    iconRight={<Icons icon='arrowRight' />}
                    className={'button-primary'}
                    onClick={selectTheme}
                    postHogTag='brand-analyzer-confirm-theme'
                >
                    {i18n.t('continue')}
                </Button>
            </div>
        </>
    )
}

const ColourPicker = ({ colour, setColour }) => {
    const [showColourPicker, setShowColourPicker] = useState(false)

    const popover = {
        position: 'absolute',
        left: 0,
        top: '40px',
        zIndex: '2'
    }

    const editorStyle = {
        default: {
            picker: {
                fontFamily: '"Open Sans", sans-serif'
            }
        }
    }

    return (
        <div onClick={() => setShowColourPicker(true)} className='color-picker' style={{ width: '100%', margin: 0 }}>
            <div className='color-picker-block' style={{ backgroundColor: colour }} />
            {showColourPicker && (
                <ClickAway style={{}} enabled={true} onClickAway={() => setShowColourPicker(false)}>
                    <div style={popover}>
                        <ChromePicker
                            onChange={(e) => setColour(e.hex)}
                            color={colour}
                            disableAlpha={true}
                            styles={editorStyle}
                        />
                    </div>
                </ClickAway>
            )}
            <div style={{ flexGrow: 1 }}>{colour}</div>
        </div>
    )
}

const ThemeBlock = ({ heading, description, children }) => (
    <Paper className='with-shadow'>
        <h4 className='mar-b5' style={{ fontSize: '16px', lineHeight: '20px' }}>
            {heading}
        </h4>
        <p className='mar-b25' style={{ color: 'rgba(89, 92, 97, 1)' }}>
            {description}
        </p>
        <div className='quickFlex' style={{ gap: '24px' }}>
            {children}
        </div>
    </Paper>
)
